import { useState } from 'react';

import { DataTable, Paginator, Chip, FilterBar } from 'components';
import { ROWS_PER_PAGE_OPTIONS } from 'constants/index';

import { displayDateTime } from '../DirectClaims/format';
import { displayAmount, statusColor } from './format';
import { GlobalClaim, GlobalClaimsParams } from './GlobalClaims.types';

import './global-claims.scss';

const STAGE_OPTIONS = [
  { label: 'All stages', value: '' },
  { label: 'Under process', value: 'Under Process' },
  { label: 'Closed', value: 'Closed' },
];

const CATEGORY_OPTIONS = [
  { label: 'All categories', value: '' },
  { label: 'Outpatient', value: 'OUT' },
  { label: 'Inpatient', value: 'IN' },
  { label: 'Dental', value: 'Dental' },
  { label: 'Maternity', value: 'Maternity' },
  { label: 'Emergency', value: 'ER' },
  { label: 'Optical', value: 'Optical' },
];

const CATEGORY_LABELS: Record<string, string> = Object.fromEntries(
  CATEGORY_OPTIONS.filter((option) => option.value).map((option) => [
    option.value,
    option.label,
  ])
);

interface GlobalClaimsUiProps {
  claims: GlobalClaim[];
  totalRecords: number;
  loading: boolean;
  errorMessage: string;
  search: string;
  params: GlobalClaimsParams;
  dates: (Date | null)[];
  handlers: {
    handleSearchChange: (name: string, value: string) => void;
    handleSubmitSearch: () => void;
    handleDateChange: (event: any) => void;
    handleStageChange: (value: string) => void;
    handleCategoryChange: (value: string) => void;
    handlePageChange: (event: any) => void;
  };
}

function referenceBody(claim: GlobalClaim) {
  return (
    <div className="global-claims__stacked">
      <span className="global-claims__strong">{claim.reference || '-'}</span>
      {claim.globalReference && (
        <span className="global-claims__muted">{claim.globalReference}</span>
      )}
    </div>
  );
}

function memberBody(claim: GlobalClaim) {
  const details = [claim.memberId, claim.relationship].filter(Boolean).join(', ');

  return (
    <div className="global-claims__stacked">
      <span className="global-claims__strong">{claim.memberName || '-'}</span>
      {details && <span className="global-claims__muted">{details}</span>}
    </div>
  );
}

function companyBody(claim: GlobalClaim) {
  return (
    <div className="global-claims__stacked global-claims__company">
      <span>{claim.company || '-'}</span>
      {claim.group && claim.group !== claim.company && (
        <span className="global-claims__muted">{claim.group}</span>
      )}
    </div>
  );
}

function statusBody(claim: GlobalClaim) {
  if (!claim.status) return claim.stage || '-';
  return <Chip label={claim.status} color={statusColor(claim)} />;
}

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="global-claims__detail">
      <span className="global-claims__detail-label">{label}</span>
      <span>{value === null || value === undefined || value === '' ? '-' : value}</span>
    </div>
  );
}

function ClaimExpansion(claim: GlobalClaim) {
  return (
    <div className="global-claims__details">
      <Detail label="Principal member" value={claim.principalName} />
      <Detail label="Policy" value={claim.policy} />
      <Detail label="Country" value={claim.country} />
      <Detail label="TPA" value={claim.tpa} />
      <Detail label="Service date" value={displayDateTime(claim.serviceDate)} />
      <Detail label="Last update" value={displayDateTime(claim.updatedAt)} />
      <Detail label="Stage" value={claim.stage} />
      <Detail label="Case" value={claim.caseDescription} />
      <Detail label="Refusal reason" value={claim.refusalReason} />
    </div>
  );
}

const COLUMNS = [
  { expander: true, field: 'expander', style: { width: '3rem' } },
  { header: 'Claim', field: 'reference', body: referenceBody },
  { header: 'Member', field: 'memberName', body: memberBody },
  { header: 'Company', field: 'company', body: companyBody },
  {
    header: 'Category',
    field: 'category',
    body: (claim: GlobalClaim) =>
      (claim.category && CATEGORY_LABELS[claim.category]) || claim.category || '-',
  },
  {
    header: 'Claim date',
    field: 'claimDate',
    body: (claim: GlobalClaim) => displayDateTime(claim.claimDate),
  },
  { header: 'Status', field: 'status', body: statusBody },
  {
    header: 'Requested',
    field: 'requestedAmount',
    className: 'global-claims__amount',
    body: (claim: GlobalClaim) => displayAmount(claim.requestedAmount, claim.currency),
  },
  {
    header: 'Accepted',
    field: 'acceptedAmount',
    className: 'global-claims__amount',
    body: (claim: GlobalClaim) => displayAmount(claim.acceptedAmount, claim.currency),
  },
];

export default function GlobalClaimsUi({
  claims,
  totalRecords,
  loading,
  errorMessage,
  search,
  params,
  dates,
  handlers,
}: GlobalClaimsUiProps) {
  const [expandedRows, setExpandedRows] = useState<any>(null);

  return (
    <div className="global-claims">
      <FilterBar
        search={{
          inputs: [
            {
              name: 'search',
              label: 'Search',
              placeholder: 'Member, member ID, claim or policy number',
              value: search,
            },
          ],
          onChange: handlers.handleSearchChange,
          onSubmit: handlers.handleSubmitSearch,
          width: 5,
        }}
        filters={[
          {
            kind: 'date',
            name: 'dates',
            placeholder: 'Claim date range',
            // Day first, matching the dates in the table.
            dateFormat: 'dd/mm/yy',
            value: dates,
            onChange: handlers.handleDateChange,
            width: 3,
          },
          {
            kind: 'dropdown',
            name: 'category',
            placeholder: 'Category',
            value: params.category,
            options: CATEGORY_OPTIONS,
            onChange: (event: any) => handlers.handleCategoryChange(event.value),
            width: 2,
          },
          {
            kind: 'dropdown',
            name: 'stage',
            placeholder: 'Stage',
            value: params.stage,
            options: STAGE_OPTIONS,
            onChange: (event: any) => handlers.handleStageChange(event.value),
            width: 2,
          },
        ]}
      />

      {errorMessage && (
        <p className="global-claims__error" role="alert">
          {errorMessage}
        </p>
      )}

      <DataTable
        value={loading ? [] : claims}
        columns={COLUMNS as any}
        dataKey="id"
        totalRecords={loading ? 0 : totalRecords}
        expandedRows={expandedRows}
        onRowToggle={(event: any) => setExpandedRows(event.data)}
        rowExpansionTemplate={ClaimExpansion}
        emptyMessage={loading ? 'Loading...' : 'No claims match these filters.'}
        stripedRows
      />

      {!loading && totalRecords > 0 && (
        <Paginator
          first={params.first}
          rows={params.rows}
          totalRecords={totalRecords}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          onPageChange={handlers.handlePageChange}
        />
      )}
    </div>
  );
}
